import { useEffect, useRef } from "react"
import { useParams } from "react-router-dom"
import { FormProvider, useForm } from "react-hook-form"
import { zodResolver } from '@hookform/resolvers/zod'
import { Button, Card, CircularProgress, Stack, Typography } from "@mui/material"
import { toast } from "react-toastify"
import { FormBlock } from "../../../../../common/FormGenerator"
import { getFormConfig } from "./settingsConfig"
import {
    ProjectDescriptionFormInput,
    descriptionSchema,
    ProjectDescriptionFormOutput,
} from "../ZodValidationSchema/DescriptionSchema"
import { CardStyle, TypStyle } from "./style"
import { useGetProjectQuery, useUpdateProjectDescriptionMutation } from "../../../../../api"
import { areProjectDescriptionFormOutputsEqual } from "../../../../../utils/project/areProjectDescriptionFormOutputsEqual"
import { ProjectStageEnum, ProjectStatusEnum, ProjectTypeEnum } from "../../../../../consts/projectTypeOptions"
import { normalizeProjectStage, normalizeProjectStatus, normalizeProjectType } from "./helpers/normalizeProjectEnums"
import { requiredTitle } from "../../../../../utils/common"
import { formatTagsForInput, parseTagsInput } from "./helpers/tags"
import { formatExternalLinksForInput, parseExternalLinksInput } from "./helpers/externalLinks"
import { EXTERNAL_LINKS_MAX } from "../../../../../consts/projectExternalLinks"

export const EditDescriptionForm = () => {
    const { id } = useParams<{ id: string }>()
    const projectId = Number(id)
    const { data: project } = useGetProjectQuery(projectId)
    const baselineRef = useRef<ProjectDescriptionFormOutput | null>(null)
    const [
        updateProjectDescription,
        { isLoading: isLoadingUpdateProjectDescription },
    ] = useUpdateProjectDescriptionMutation()

    const methods = useForm<ProjectDescriptionFormInput, unknown, ProjectDescriptionFormOutput>({
        mode: 'onBlur',
        resolver: zodResolver(descriptionSchema),
        defaultValues: {
            name: "",
            stage: ProjectStageEnum.IDEA,
            region: "",
            description: "",
            full_description: "",
            env_factor: "",
            expected_result: "",
            initial_state: "",
            necessary_actions: "",
            implementation: "",
            tags: "",
            city: "",
            address: "",
            website: "",
            external_links: "",
            status: ProjectStatusEnum.DRAFT,
            type: ProjectTypeEnum.ONE_TIME,
        },
    })

    const { handleSubmit, reset, getValues, setValue } = methods

    useEffect(() => {
        if (!project) {
            return
        }
        reset({
            name: project?.name ?? "",
            type: normalizeProjectType(project?.type),
            stage: normalizeProjectStage(String(project?.stage)),
            region: project?.region ?? "",
            description: project?.about?.description ?? "",
            full_description: project?.about?.full_description ?? "",
            env_factor: project?.about?.env_factor ?? "",
            expected_result: project?.about?.expected_result ?? "",
            initial_state: project?.about?.initial_state ?? "",
            necessary_actions: project?.about?.necessary_actions ?? "",
            implementation: project?.about?.implementation ?? "",
            tags: formatTagsForInput((project as any)?.tags ?? []),
            city: project?.city ?? "",
            address: project?.address ?? "",
            website: project?.website ?? "",
            external_links: formatExternalLinksForInput(project?.external_links ?? []),
            status: normalizeProjectStatus(project?.status),
        })
        baselineRef.current = getValues() as ProjectDescriptionFormOutput
    }, [project, reset, getValues])

    const onSubmitDraft = async (data: ProjectDescriptionFormOutput) => {
        if (baselineRef.current !== null && areProjectDescriptionFormOutputsEqual(data, baselineRef.current)) {
            toast.info("Нет изменений")
            return
        }
        if (
            data.status === ProjectStatusEnum.PUBLISHED
            && !(typeof project?.image === 'string' && project.image.trim().length > 0)
        ) {
            toast.info('Загрузите аватар проекта')
            return
        }
        try {
            const {
                tags: _tagsRaw,
                website: _websiteRaw,
                external_links: _externalLinksRaw,
                full_description,
                ...rest
            } = data
            const tagsInput = (data.tags ?? "").trim()
            const parsedTags = tagsInput ? parseTagsInput(tagsInput) : []
            const websiteTrimmed = data.website.trim()
            const parsedExternalLinks = parseExternalLinksInput(data.external_links ?? "")
            const fullDescriptionTrimmed = (full_description ?? "").trim()

            await updateProjectDescription({
                projectId,
                body: {
                    ...rest,
                    full_description: fullDescriptionTrimmed ? fullDescriptionTrimmed : null,
                    tags: parsedTags,
                    external_links: parsedExternalLinks,
                    ...(websiteTrimmed ? { website: websiteTrimmed } : { website: null }),
                },
            }).unwrap()
            const prettyExternalLinks = formatExternalLinksForInput(parsedExternalLinks)
            setValue('external_links', prettyExternalLinks, { shouldDirty: false, shouldValidate: true })
            toast.success("Описание проекта обновлено")
            baselineRef.current = {
                ...data,
                external_links: prettyExternalLinks,
            }
        } catch {
            toast.error('Не удалось обновить описание проекта')
        }
    }

    return (
        <FormProvider {...methods}>
            <Stack
                component="form"
                gap={2}
                onSubmit={handleSubmit(onSubmitDraft)}
            >
                <Card variant="primaryAdaptiveCard" sx={CardStyle}>
                    <Typography
                        variant="black-16-semiBold"
                        component="div"
                        sx={TypStyle}
                    >
                        {requiredTitle("Название проекта")}
                    </Typography>
                    <FormBlock formFieldConfig={getFormConfig('name')} />
                </Card>
                <Card variant="primaryAdaptiveCard" sx={CardStyle}>
                    <Typography
                        variant="black-16-semiBold"
                        component="div"
                        sx={TypStyle}
                    >
                        Статус проекта
                    </Typography>
                    <FormBlock formFieldConfig={getFormConfig('status')} />
                </Card>
                <Card variant="primaryAdaptiveCard" sx={CardStyle}>
                    <Typography
                        variant="black-16-semiBold"
                        component="div"
                        sx={TypStyle}
                    >
                        Тип проекта
                    </Typography>
                    <FormBlock formFieldConfig={getFormConfig('type')} />
                </Card>
                <Card variant="primaryAdaptiveCard" sx={CardStyle}>
                    <Typography
                        variant="black-16-semiBold"
                        component="div"
                        sx={TypStyle}
                    >
                        Этап проекта
                    </Typography>
                    <FormBlock formFieldConfig={getFormConfig("stage")} />
                </Card>
                <Card variant="primaryAdaptiveCard" sx={CardStyle}>
                    <Typography
                        variant="black-16-semiBold"
                        component="div"
                        sx={TypStyle}
                    >
                        {requiredTitle("Местоположение проекта")}
                    </Typography>
                    <FormBlock formFieldConfig={getFormConfig('region')} />
                </Card>
                <Card variant="primaryAdaptiveCard" sx={CardStyle}>
                    <Typography
                        variant="black-16-semiBold"
                        component="div"
                        sx={TypStyle}
                    >
                        Город
                    </Typography>
                    <FormBlock formFieldConfig={getFormConfig('city')} />
                </Card>
                <Card variant="primaryAdaptiveCard" sx={CardStyle}>
                    <Typography
                        variant="black-16-semiBold"
                        component="div"
                        sx={TypStyle}
                    >
                        Адрес
                    </Typography>
                    <FormBlock formFieldConfig={getFormConfig('address')} />
                </Card>
                <Card variant="primaryAdaptiveCard" sx={CardStyle}>
                    <Typography
                        variant="black-16-semiBold"
                        component="div"
                        sx={TypStyle}
                    >
                        {requiredTitle("Краткое описание")}
                    </Typography>
                    <FormBlock formFieldConfig={getFormConfig('description')} />
                </Card>
                <Card variant="primaryAdaptiveCard" sx={CardStyle}>
                    <Typography
                        variant="black-16-semiBold"
                        component="div"
                        sx={TypStyle}
                    >
                        Полное описание
                    </Typography>
                    <FormBlock formFieldConfig={getFormConfig('full_description')} />
                </Card>
                <Card variant="primaryAdaptiveCard" sx={CardStyle}>
                    <Typography
                        variant="black-16-semiBold"
                        component="div"
                        sx={TypStyle}
                    >
                        {requiredTitle("Проблема/возможность (фактор среды)")}
                    </Typography>
                    <FormBlock formFieldConfig={getFormConfig('env_factor')} />
                </Card>
                <Card variant="primaryAdaptiveCard" sx={CardStyle}>
                    <Typography
                        variant="black-16-semiBold"
                        component="div"
                        sx={TypStyle}
                    >
                        {requiredTitle("Что должно получиться?")}
                    </Typography>
                    <FormBlock formFieldConfig={getFormConfig('expected_result')} />
                </Card>
                <Card variant="primaryAdaptiveCard" sx={CardStyle}>
                    <Typography
                        variant="black-16-semiBold"
                        component="div"
                        sx={TypStyle}
                    >
                        {requiredTitle("Исходное положение дел")}
                    </Typography>
                    <FormBlock formFieldConfig={getFormConfig('initial_state')} />
                </Card>
                <Card variant="primaryAdaptiveCard" sx={CardStyle}>
                    <Typography
                        variant="black-16-semiBold"
                        component="div"
                        sx={TypStyle}
                    >
                        {requiredTitle("Что необходимо сделать по проекту?")}
                    </Typography>
                    <FormBlock formFieldConfig={getFormConfig('necessary_actions')} />
                </Card>
                <Card variant="primaryAdaptiveCard" sx={CardStyle}>
                    <Typography
                        variant="black-16-semiBold"
                        component="div"
                        sx={TypStyle}
                    >
                        {requiredTitle("Как будем реализовывать?")}
                    </Typography>
                    <FormBlock formFieldConfig={getFormConfig('implementation')} />
                </Card>
                <Card variant="primaryAdaptiveCard" sx={CardStyle}>
                    <Typography
                        variant="black-16-semiBold"
                        component="div"
                        sx={TypStyle}
                    >
                        Тэги (максимально 10)
                    </Typography>
                    <FormBlock formFieldConfig={getFormConfig('tags')} />
                </Card>
                <Card variant="primaryAdaptiveCard" sx={CardStyle}>
                    <Typography
                        variant="black-16-semiBold"
                        component="div"
                        sx={TypStyle}
                    >
                        Веб-сайт проекта
                    </Typography>
                    <FormBlock formFieldConfig={getFormConfig('website')} />
                </Card>
                <Card variant="primaryAdaptiveCard" sx={CardStyle}>
                    <Typography
                        variant="black-16-semiBold"
                        component="div"
                        sx={TypStyle}
                    >
                        {`Дополнительные ссылки (необязательно, до ${EXTERNAL_LINKS_MAX})`}
                    </Typography>
                    <FormBlock formFieldConfig={getFormConfig('external_links')} />
                </Card>

                <Stack direction="row" spacing={1}>
                    <Button
                        type="submit"
                        variant="follow"
                        disabled={isLoadingUpdateProjectDescription}
                    >
                        {isLoadingUpdateProjectDescription ? (
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <CircularProgress size={16} color="inherit" />
                                <span>Сохранение...</span>
                            </Stack>
                        ) : (
                            "Сохранить"
                        )}
                    </Button>
                </Stack>
            </Stack>
        </FormProvider>
    )
}